import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControl,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
// Removed direct Firestore imports - using secure server functions instead
import { getUserFacilities, assignUserFacilities, getUserFacilityAssignments } from '../../services/userService';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2
};

const facilitiesListStyle = {
  maxHeight: '300px',
  overflow: 'auto',
  mt: 2,
  p: 2,
  bgcolor: '#f5f5f5',
  borderRadius: 1
};

const AssignFacilityModal = ({ open, onClose, user }) => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [error, setError] = useState(null);
  
  const filteredFacilities = facilities.filter(facility => 
    facility.name.toLowerCase().includes(filterText.toLowerCase())
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching facilities and assignments for user:', user.email);
        
        // Fetch available facilities using secure server function
        // Server automatically determines what facilities the current user can assign based on their role
        const facilitiesResult = await getUserFacilities(1, 1000); // Get up to 1000 facilities
        console.log('Available facilities:', facilitiesResult.facilities);
        setFacilities(facilitiesResult.facilities || []);
        
        // Fetch user's current facility assignments using secure function
        const currentAssignments = await getUserFacilityAssignments(user.id);
        console.log('Current assignments:', currentAssignments);
        setSelectedFacilities(currentAssignments);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to fetch data');
        setLoading(false);
      }
    };

    fetchData();
  }, [open, user]);

  const handleCheckboxChange = (facilityId) => {
    setSelectedFacilities(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      } else {
        return [...prev, facilityId];
      }
    });
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      console.log('Assigning facilities to user:', user.email, selectedFacilities);
      
      // Use secure server function to assign facilities
      // Server validates permissions and ensures data integrity
      const result = await assignUserFacilities(user.id, selectedFacilities);
      
      console.log('Assignment successful:', result);
      onClose(true); // Pass true to indicate successful update
    } catch (error) {
      console.error('Error updating user facilities:', error);
      
      // Show specific error message to user
      let errorMessage = 'Failed to update user facilities';
      if (error.code === 'permission-denied') {
        errorMessage = 'You do not have permission to assign these facilities';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid facility selection';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Modal open={open} onClose={() => onClose(false)}>
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="assign-facility-modal"
    >
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2" gutterBottom>
          Assign Facilities to {user?.email}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <FormControl fullWidth>
          <TextField
            fullWidth
            placeholder="Search facilities..."
            value={filterText}
            onChange={handleFilterChange}
            disabled={saving}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Paper sx={facilitiesListStyle}>
            <FormGroup>
              {filteredFacilities.map((facility) => (
                <FormControlLabel
                  key={facility.id}
                  control={
                    <Checkbox
                      checked={selectedFacilities.includes(facility.id)}
                      onChange={() => handleCheckboxChange(facility.id)}
                      disabled={saving}
                    />
                  }
                  label={facility.name}
                />
              ))}
              {filteredFacilities.length === 0 && !loading && (
                <Typography color="text.secondary" sx={{ p: 1 }}>
                  {facilities.length === 0 
                    ? "No facilities available for assignment" 
                    : "No facilities found matching your search"}
                </Typography>
              )}
            </FormGroup>
          </Paper>
        </FormControl>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AssignFacilityModal;
